import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import { MessageService } from 'src/app/service/message.service';
import { UserService } from 'src/app/service/user.service';
import {
  Country,
  COUNTRIES,
  DEFAULT_COUNTRY,
  getPhonePlaceholder,
  matchCountryByDigitPrefix,
} from 'src/app/util/country-codes';

/**
 * 어떤 도메인의 모바일을 인증할지 결정.
 * - 'user': /auth/sms · /auth/sms/check — user.mobile / user.phoneNumber 갱신 (회원 본인 인증용. 기본값)
 * - 'host_info': /api/hostInfo/me/mobile/sms · /check — host_info.mobile만 갱신 (호스트 알림 수신 채널)
 */
export type MobileVerificationTarget = 'user' | 'host_info';

export interface MobileVerificationBottomSheetData {
  initialPhone?: string;
  target?: MobileVerificationTarget;
}

export interface MobileVerificationResult {
  verified: boolean;
  e164Phone?: string;
}

const VERIFY_TIMEOUT_SECONDS = 5 * 60;
const RESEND_COOLDOWN_SECONDS = 30;
const AUTH_CODE_LENGTH = 6;
const AUTH_CODE_GROUP_SIZE = 3;

type Step = 'PHONE' | 'CODE';

@Component({
  selector: 'mobile-verification-bottom-sheet',
  templateUrl: './mobile-verification-bottom-sheet.component.html',
  styleUrls: ['./mobile-verification-bottom-sheet.component.scss'],
})
export class MobileVerificationBottomSheetComponent implements OnInit, OnDestroy {
  readonly countries: readonly Country[] = COUNTRIES;
  /** 검색어로 좁힌 목록. 목록이 245개라 *ngFor 가 매 변경감지마다 필터를 돌지 않도록 필드에 담는다. */
  filteredCountries: readonly Country[] = COUNTRIES;
  countrySearch = '';
  readonly codeLength = AUTH_CODE_LENGTH;
  readonly codeGroupSize = AUTH_CODE_GROUP_SIZE;
  readonly codeCellIndexes = Array.from({ length: AUTH_CODE_LENGTH }, (_, i) => i);
  selectedCountry: Country = DEFAULT_COUNTRY;
  isCountryOpen = false;
  // 키보드 네비게이션용 활성 인덱스 (selected와 별개 — selected는 현재 적용된 값, active는 이동 커서)
  activeCountryIndex = -1;
  // 트리거 위치 기준으로 드롭다운을 아래/위 어느 쪽에 펼칠지 결정
  dropdownDirection: 'down' | 'up' = 'down';
  @ViewChild('countryTriggerRef') private countryTriggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('countrySearchRef') private countrySearchRef?: ElementRef<HTMLInputElement>;
  @ViewChildren('countryOptionItem') private countryOptionItems?: QueryList<ElementRef<HTMLLIElement>>;

  step: Step = 'PHONE';

  formGroup: UntypedFormGroup;
  codeDigits: string[] = Array.from({ length: AUTH_CODE_LENGTH }, () => '');
  isCodeFocused = false;

  @ViewChild('codeInput') codeInputRef!: ElementRef<HTMLInputElement>;

  isRequesting = false;
  isVerifying = false;

  remainSeconds = 0;
  resendCooldownSeconds = 0;
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private readonly destroy$ = new Subject<void>();
  // 한 번 발급된 인증 코드에 대해 자동 제출은 1회만 시도 (수동 재시도는 인증하기 버튼으로)
  private autoSubmitted = false;

  constructor(
    private bottomSheetRef: MatBottomSheetRef<MobileVerificationBottomSheetComponent, MobileVerificationResult>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: MobileVerificationBottomSheetData,
    private authService: AuthService,
    private userService: UserService,
    private messageService: MessageService,
  ) {
    this.formGroup = new UntypedFormGroup({
      // 숫자/하이픈/공백/+/괄호/점만 허용 (일반 전화 표기 모두 커버). E.164 유효성은
      // libphonenumber-js로 buildE164Phone에서 별도 검증.
      localPhone: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[+\d\s\-().]{6,30}$/)]),
      authNumber: new UntypedFormControl('', [
        Validators.required,
        Validators.pattern(new RegExp(`^[0-9]{${AUTH_CODE_LENGTH}}$`)),
      ]),
    });
  }

  ngOnInit(): void {
    const { country, localPhone } = this.parseInitialPhone((this.data?.initialPhone ?? '').trim());
    this.selectedCountry = country ?? DEFAULT_COUNTRY;
    this.formGroup.controls.localPhone.setValue(localPhone);
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isPhoneLocked(): boolean {
    return this.step === 'CODE';
  }

  private parseInitialPhone(initial: string): { country?: Country; localPhone: string } {
    if (!initial) return { localPhone: '' };

    // 1) libphonenumber-js로 우선 파싱
    const parsed = parsePhoneNumberFromString(initial.startsWith('+') ? initial : `+${initial}`);
    if (parsed?.country) {
      const matched = this.countries.find((c) => c.iso2 === parsed.country);
      if (matched) return { country: matched, localPhone: parsed.nationalNumber };
    }

    // 2) `+`로 시작 — dialCode prefix 매칭
    if (initial.startsWith('+')) {
      const stripped = initial.replace(/^\+/, '');
      const matched = matchCountryByDigitPrefix(stripped);
      if (matched) {
        return { country: matched, localPhone: stripped.substring(matched.dialCode.length) };
      }
    }

    // 3) 한국 로컬 포맷 (010xxxxxxxx 등) — KR로 fallback
    if (/^0\d{8,10}$/.test(initial)) {
      return { country: DEFAULT_COUNTRY, localPhone: initial.replace(/^0/, '') };
    }

    // 4) 그 외 — 숫자만 추출해 입력란에 그대로 노출
    return { localPhone: initial.replace(/[^0-9]/g, '') };
  }

  toggleCountryDropdown(event: Event): void {
    event.stopPropagation();
    if (this.isPhoneLocked) return;
    if (this.isCountryOpen) {
      this.closeCountryDropdown(false);
    } else {
      this.openCountryDropdown();
    }
  }

  private openCountryDropdown(): void {
    if (this.isPhoneLocked) return;
    this.isCountryOpen = true;
    this.countrySearch = '';
    this.filteredCountries = this.countries;
    const idx = this.countries.findIndex((c) => c.iso2 === this.selectedCountry.iso2);
    this.activeCountryIndex = idx >= 0 ? idx : 0;
    this.computeDropdownDirection();
    // 245개 목록에서 다음 동작은 거의 항상 타이핑이다 — 마우스로 열었든 키보드로 열었든 검색창을 잡는다.
    // (Arrow 로 목록에 들어가면 focusActiveOption 이 포커스를 li 로 옮긴다.)
    setTimeout(() => this.countrySearchRef?.nativeElement?.focus(), 0);
  }

  /** 목록이 타이핑에 따라 줄어드는 것은 화면낭독기에 들리지 않는다 — 건수를 읽어 준다. */
  get countryResultLabel(): string {
    return this.filteredCountries.length > 0 ? `검색 결과 ${this.filteredCountries.length}개` : '검색 결과가 없습니다';
  }

  /** 화면에 +82 로 보이므로 '+' 를 붙여 쳐도 매칭된다. */
  onCountrySearch(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.countrySearch = raw;
    const needle = raw.trim().toLowerCase();
    const dialDigits = needle.replace(/[^0-9]/g, '');
    const matched = needle
      ? this.countries.filter(
          (c) =>
            c.name.toLowerCase().includes(needle) ||
            c.iso2.toLowerCase().includes(needle) ||
            (dialDigits !== '' && c.dialCode.includes(dialDigits)),
        )
      : this.countries;
    // 부분 일치를 그대로 두면 이름 가나다순이라 `7` 에 나우루(+674)가 러시아(+7)보다 앞선다.
    const rank = (c: Country) => (c.dialCode === dialDigits ? 0 : c.dialCode.startsWith(dialDigits) ? 1 : 2);
    this.filteredCountries = dialDigits ? [...matched].sort((a, b) => rank(a) - rank(b)) : matched;
    this.activeCountryIndex = this.filteredCountries.length > 0 ? 0 : -1;
  }

  private closeCountryDropdown(returnFocus = true): void {
    if (!this.isCountryOpen) return;
    this.isCountryOpen = false;
    this.activeCountryIndex = -1;
    this.countrySearch = '';
    this.filteredCountries = this.countries;
    if (returnFocus) {
      // 키보드 흐름이 끊기지 않도록 trigger로 포커스 복귀
      setTimeout(() => this.countryTriggerRef?.nativeElement?.focus(), 0);
    }
  }

  /**
   * 트리거 위치에서 뷰포트 하단까지의 여유와 드롭다운 max-height를 비교해 펼침 방향 결정.
   * 하단 공간이 부족하고 상단이 더 넓으면 위로 펼침(.up).
   */
  private computeDropdownDirection(): void {
    const trigger = this.countryTriggerRef?.nativeElement;
    if (!trigger) {
      this.dropdownDirection = 'down';
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const DROPDOWN_MAX_HEIGHT = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    this.dropdownDirection = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow ? 'up' : 'down';
  }

  /** 활성 option 의 id — 검색창의 aria-activedescendant 가 가리킨다. */
  get activeCountryOptionId(): string | null {
    return this.activeCountryIndex >= 0 && this.activeCountryIndex < this.filteredCountries.length
      ? `country-option-${this.activeCountryIndex}`
      : null;
  }

  /**
   * 포커스는 검색창에 두고 활성 option 만 옮긴다(combobox + aria-activedescendant).
   * li 로 포커스를 옮기면 화면낭독기에는 읽히지만 그 뒤로는 검색어를 더 칠 수 없다.
   */
  private focusActiveOption(): void {
    const items = this.countryOptionItems?.toArray();
    const target = items?.[this.activeCountryIndex]?.nativeElement;
    if (target) {
      target.scrollIntoView({ block: 'nearest' });
    }
  }

  /** trigger 위 키보드 — 표준 select 패턴 (Enter/Space/Arrow 열기, Esc 닫기) */
  onCountryTriggerKeydown(event: KeyboardEvent): void {
    if (this.isPhoneLocked) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        this.openCountryDropdown();
        break;
      case 'Escape':
        if (this.isCountryOpen) {
          event.preventDefault();
          this.closeCountryDropdown();
        }
        break;
    }
  }

  /**
   * 드롭다운 키보드 — 포커스는 검색창에 머문다(combobox). 방향키로 활성 항목을 옮기고
   * Enter 로 고른다. 스페이스·Home·End 는 가로채지 않는다 — 가로채면 이름에 공백이 있는
   * 나라(`중앙 아프리카 공화국` 등 30개)를 칠 수 없고 캐럿도 못 옮긴다.
   */
  onCountryDropdownKeydown(event: KeyboardEvent): void {
    const max = this.filteredCountries.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (max < 0) break;
        this.activeCountryIndex = Math.min(max, this.activeCountryIndex + 1);
        this.focusActiveOption();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (max < 0) break;
        this.activeCountryIndex = Math.max(0, this.activeCountryIndex - 1);
        this.focusActiveOption();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.activeCountryIndex >= 0 && this.activeCountryIndex <= max) {
          this.onCountrySelect(this.filteredCountries[this.activeCountryIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeCountryDropdown();
        break;
      case 'Tab':
        // Tab으로 빠져나가면 닫되 포커스는 브라우저 흐름에 맡김 (trigger로 되돌리지 않음)
        this.closeCountryDropdown(false);
        break;
    }
  }

  onCountrySelect(country: Country | undefined): void {
    // 템플릿이 selectedCountry.iso2 를 읽으므로 undefined 가 들어가면 바인딩에서 TypeError 가 난다.
    if (!country) return;
    this.selectedCountry = country;
    this.closeCountryDropdown();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.isCountryOpen) {
      this.closeCountryDropdown(false);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isCountryOpen) this.computeDropdownDirection();
  }

  buildE164Phone(): string | undefined {
    const raw = this.getLocalPhoneDigits();
    if (!raw) return undefined;

    // libphonenumber-js가 국가별 national prefix(예: 한국 leading 0)를 자동 제거
    const parsed = parsePhoneNumberFromString(raw, this.selectedCountry.iso2);
    if (!parsed || !parsed.isValid()) return undefined;
    return parsed.number;
  }

  requestCode(): void {
    if (this.isRequesting) return;
    if (this.formGroup.controls.localPhone.invalid) {
      this.formGroup.controls.localPhone.markAsTouched();
      this.messageService.toast('전화번호를 정확히 입력해주세요.');
      return;
    }

    const e164 = this.buildE164Phone();
    if (!e164) {
      this.messageService.toast('전화번호 형식이 올바르지 않습니다.');
      return;
    }

    const isResend = this.step === 'CODE';

    this.isRequesting = true;
    const target: MobileVerificationTarget = this.data?.target ?? 'user';
    const request$ =
      target === 'host_info' ? this.userService.requestHostInfoMobileSms(e164) : this.authService.requestAuthSms(e164);
    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isRequesting = false)),
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.messageService.toast('인증번호 발송에 실패했습니다. 잠시 후 다시 시도해주세요.');
            return;
          }
          this.step = 'CODE';
          this.resetCodeCells();
          this.startTimer();
          this.messageService.toast(isResend ? '인증번호를 재전송했습니다.' : '인증번호가 발송되었습니다.');
          setTimeout(() => this.focusCodeInput(), 0);
        },
        error: () => {
          this.messageService.toast('인증번호 발송에 실패했습니다. 잠시 후 다시 시도해주세요.');
        },
      });
  }

  verifyCode(): void {
    if (this.isVerifying) return;
    if (this.isTimerExpired) {
      this.messageService.toast('인증번호가 만료되었습니다. 다시 요청해주세요.');
      return;
    }
    if (this.formGroup.controls.authNumber.invalid) {
      this.formGroup.controls.authNumber.markAsTouched();
      this.messageService.toast(`${AUTH_CODE_LENGTH}자리 인증번호를 입력해주세요.`);
      return;
    }

    const e164 = this.buildE164Phone();
    if (!e164) {
      this.messageService.toast('전화번호를 다시 확인해주세요.');
      return;
    }

    const authNumber: string = this.formGroup.controls.authNumber.value ?? '';

    this.isVerifying = true;
    const target: MobileVerificationTarget = this.data?.target ?? 'user';
    const check$ =
      target === 'host_info'
        ? this.userService.checkHostInfoMobileSms(e164, authNumber)
        : this.authService.checkAuthSms(e164, authNumber);
    check$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isVerifying = false)),
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.messageService.toast('인증번호가 일치하지 않습니다.');
            return;
          }
          this.clearTimer();
          this.messageService.toast('휴대폰 번호가 인증되었습니다.');
          this.bottomSheetRef.dismiss({ verified: true, e164Phone: e164 });
        },
        error: () => {
          this.messageService.toast('인증에 실패했습니다. 잠시 후 다시 시도해주세요.');
        },
      });
  }

  editPhone(): void {
    this.step = 'PHONE';
    this.clearTimer();
    this.resetCodeCells();
  }

  close(): void {
    this.bottomSheetRef.dismiss({ verified: false });
  }

  get remainTimeLabel(): string {
    const minutes = Math.floor(this.remainSeconds / 60);
    const seconds = this.remainSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get isTimerExpired(): boolean {
    return this.step === 'CODE' && this.remainSeconds <= 0;
  }

  get phonePlaceholder(): string {
    return getPhonePlaceholder(this.selectedCountry.iso2);
  }

  private startTimer(): void {
    this.clearTimer();
    this.remainSeconds = VERIFY_TIMEOUT_SECONDS;
    this.resendCooldownSeconds = RESEND_COOLDOWN_SECONDS;
    this.timerInterval = setInterval(() => {
      this.remainSeconds = Math.max(0, this.remainSeconds - 1);
      this.resendCooldownSeconds = Math.max(0, this.resendCooldownSeconds - 1);
      if (this.remainSeconds <= 0) {
        this.clearTimer();
      }
    }, 1000);
  }

  get canResend(): boolean {
    return !this.isRequesting && this.resendCooldownSeconds <= 0;
  }

  get resendLabel(): string {
    if (this.isRequesting) return '재전송 중...';
    if (this.resendCooldownSeconds > 0) return `${this.resendCooldownSeconds}초 후 재전송`;
    return '인증번호 재전송';
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private getLocalPhoneDigits(): string {
    const value: string = this.formGroup.controls.localPhone.value ?? '';
    return value.toString().replace(/[^0-9]/g, '');
  }

  onLocalPhoneInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const rawInput = target.value;

    // 1) 허용 문자(숫자/하이픈/공백/+/괄호/점)만 남기고 그 외는 제거 — 영문/특수문자 입력 차단.
    //    type=tel은 모바일 키패드만 띄울 뿐 PC 키보드 입력은 막지 않으므로 명시적 sanitize 필요.
    //    "(010) 1234-5678", "+1 (415) 555-0123" 같은 일반적 표기를 모두 허용.
    //
    //    다른 문서/메신저에서 복붙할 때 유니코드 dash(en-dash –, em-dash —, minus −)나
    //    non-breaking space가 섞여 들어올 수 있어, 막는 대신 ASCII 등가 문자로 정규화한 뒤
    //    sanitize. 사용자가 "복붙했는데 왜 안 들어가지?" 혼란을 겪지 않도록.
    const normalized = rawInput
      // U+2010 hyphen ~ U+2015 horizontal bar(en/em-dash 포함) + U+2212 minus -> ASCII '-'
      .replace(/[\u2010-\u2015\u2212]/g, '-')
      // non-breaking space류 (NBSP/figure/narrow) -> 일반 ' '
      .replace(/[\u00A0\u2007\u202F]/g, ' ');
    const sanitized = normalized.replace(/[^0-9+\s\-().]/g, '');
    if (sanitized !== rawInput) {
      target.value = sanitized;
    }

    // 2) "+" 또는 "00"으로 시작하면 국제 표기로 간주하고 국가 자동 매칭
    const intlPrefix = sanitized.startsWith('+') ? '+' : sanitized.startsWith('00') ? '00' : '';
    if (intlPrefix) {
      const digits = sanitized.replace(/[^0-9]/g, '');
      const withoutIntlPrefix = intlPrefix === '00' ? digits.replace(/^00/, '') : digits;
      const matched = matchCountryByDigitPrefix(withoutIntlPrefix);
      if (matched) {
        // 매칭되면 국가 변경 + 입력란은 로컬 번호만 남김
        this.selectedCountry = matched;
        const localPart = withoutIntlPrefix.substring(matched.dialCode.length);
        this.formGroup.controls.localPhone.setValue(localPart);
        return;
      }
      // 아직 dialCode가 인식 안 됨 (예: 사용자가 "+"만 친 순간) — 사용자 입력을 보존해 다음 키 입력을 기다림
      // 주의: Reactive Forms의 setValue는 DOM input.value를 자동 동기화하므로 setValue 호출 뒤에 target.value를 덮어써야 함
      const preserved = intlPrefix + withoutIntlPrefix;
      this.formGroup.controls.localPhone.setValue('');
      target.value = preserved;
      return;
    }

    // 3) 일반 입력 — 사용자가 친 하이픈/공백 유지해서 폼에 반영.
    //    E.164 변환은 buildE164Phone()에서 libphonenumber-js가 알아서 숫자만 추출.
    this.formGroup.controls.localPhone.setValue(sanitized);
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/[^0-9]/g, '').slice(0, AUTH_CODE_LENGTH);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.codeDigits = Array.from({ length: AUTH_CODE_LENGTH }, (_, i) => digits[i] ?? '');
    this.syncAuthNumberControl();
  }

  onCodeFocus(): void {
    this.isCodeFocused = true;
  }

  onCodeBlur(): void {
    this.isCodeFocused = false;
  }

  focusCodeInput(): void {
    this.codeInputRef?.nativeElement?.focus();
  }

  get focusedCellIndex(): number {
    if (!this.isCodeFocused) return -1;
    const len = this.codeDigits.filter((d) => !!d).length;
    return Math.min(len, AUTH_CODE_LENGTH - 1);
  }

  get codeRaw(): string {
    return this.codeDigits.join('');
  }

  private syncAuthNumberControl(): void {
    this.formGroup.controls.authNumber.setValue(this.codeRaw);
    this.tryAutoSubmit();
  }

  private tryAutoSubmit(): void {
    if (this.autoSubmitted) return;
    if (this.isVerifying || this.isTimerExpired) return;
    if (!this.codeDigits.every((d) => !!d)) return;
    this.autoSubmitted = true;
    this.verifyCode();
  }

  private resetCodeCells(): void {
    this.codeDigits = Array.from({ length: AUTH_CODE_LENGTH }, () => '');
    this.autoSubmitted = false;
    if (this.codeInputRef) {
      this.codeInputRef.nativeElement.value = '';
    }
    this.formGroup.controls.authNumber.reset('');
  }
}
